import React, { useState } from 'react';
import axios from 'axios';
import { Upload, Download, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportExport = () => {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const { toast } = useToast();
  const { t } = useI18n();

  const handleImport = async (type, file) => {
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/${type}/bulk/import`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      setImportResult(response.data);
      toast({
        title: t('pages.importExport.importComplete','Import Complete!'),
        description: `Imported ${response.data.imported} ${type}, ${response.data.failed} failed.`,
      });
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: t('pages.importExport.importFailed','Import Failed'),
        description: error.response?.data?.detail || "Failed to import file",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API}/${type}/export/csv`, {
        withCredentials: true,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bissa_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: t('pages.importExport.exportComplete','Export Complete!'),
        description: `${type} exported successfully.`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: t('pages.importExport.exportFailed','Export Failed'),
        description: "Failed to export data",
        variant: "destructive",
      });
    }
  };

  const downloadTemplate = (type) => {
    let csvContent = '';
    
    if (type === 'contacts') {
      csvContent = 'name,email,phone,company,status,tags,notes\n';
      csvContent += 'John Doe,john@example.com,555-0100,Acme Corp,lead,"tag1,tag2",Sample note\n';
    } else if (type === 'companies') {
      csvContent += 'name,domain,industry,size,phone,address,notes\n';
      csvContent += 'Acme Corp,acme.com,Technology,51-200,555-0100,"123 Main St",Sample note\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bissa_${type}_template.csv`;
    link.click();

    toast({
      title: t('pages.importExport.templateDownloaded','Template Downloaded'),
      description: `${type} CSV template downloaded successfully.`,
    });
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages.importExport.title','Import & Export')}</h1>
        <p className="text-gray-600 mt-2">{t('pages.importExport.subtitle','Bulk manage your data with CSV files')}</p>
      </div>

      {/* Import Result */}
      {importResult && (
        <Card className="mb-6 border-2 border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-600 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 mb-2">Import Complete!</h3>
                <div className="text-sm text-green-800 space-y-1">
                  <p>✅ Successfully imported: <strong>{importResult.imported}</strong> records</p>
                  {importResult.failed > 0 && (
                    <p>❌ Failed: <strong>{importResult.failed}</strong> records</p>
                  )}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer font-medium">View Errors</summary>
                      <div className="mt-2 space-y-1 text-xs">
                        {importResult.errors.map((error, idx) => (
                          <p key={idx}>• {error}</p>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setImportResult(null)}
              >
                ✕
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contacts Import/Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t('pages.importExport.contacts','Contacts')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Import Section */}
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg">
              <h4 className="font-medium mb-2">{t('pages.importExport.importContacts','Import Contacts')}</h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('pages.importExport.uploadHelp','Upload a CSV file to import multiple contacts at once')}
              </p>
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    id="contacts-upload"
                    type="file"
                    accept=".csv"
                    onChange={(e) => e.target.files[0] && handleImport('contacts', e.target.files[0])}
                    disabled={importing}
                    className="hidden"
                  />
                  <label htmlFor="contacts-upload" className="block">
                    <div className="w-full px-4 py-2 bg-[#0176D3] hover:bg-[#0155A8] text-white rounded-md cursor-pointer text-center font-medium transition-colors flex items-center justify-center gap-2">
                      <Upload className="w-4 h-4" />
                      {importing ? t('pages.importExport.importing','Importing...') : t('pages.importExport.uploadCsv','Upload CSV')}
                    </div>
                  </label>
                </div>
                <Button
                  variant="outline"
                  onClick={() => downloadTemplate('contacts')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('pages.importExport.template','Template')}
                </Button>
              </div>
            </div>

            {/* Export Section */}
            <div className="p-4 bg-gray-50 border rounded-lg">
              <h4 className="font-medium mb-2">{t('pages.importExport.exportContacts','Export Contacts')}</h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('pages.importExport.exportContactsDesc','Download all your contacts as a CSV file')}
              </p>
              <Button
                onClick={() => handleExport('contacts')}
                variant="outline"
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('pages.importExport.exportCsv','Export to CSV')}
              </Button>
            </div>

            {/* Info */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-medium mb-1">Supported Formats:</p>
                <p>• LeadGen Pro exports (auto-detected)</p>
                <p>• Standard CSV: name, email, phone, company, status, tags, notes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Companies Import/Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t('pages.importExport.companies','Companies')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Import Section */}
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg">
              <h4 className="font-medium mb-2">{t('pages.importExport.importCompanies','Import Companies')}</h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('pages.importExport.uploadCompaniesHelp','Upload a CSV file to import multiple companies at once')}
              </p>
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    id="companies-upload"
                    type="file"
                    accept=".csv"
                    onChange={(e) => e.target.files[0] && handleImport('companies', e.target.files[0])}
                    disabled={importing}
                    className="hidden"
                  />
                  <label htmlFor="companies-upload" className="block">
                    <div className="w-full px-4 py-2 bg-[#0176D3] hover:bg-[#0155A8] text-white rounded-md cursor-pointer text-center font-medium transition-colors flex items-center justify-center gap-2">
                      <Upload className="w-4 h-4" />
                      {importing ? t('pages.importExport.importing','Importing...') : t('pages.importExport.uploadCsv','Upload CSV')}
                    </div>
                  </label>
                </div>
                <Button
                  variant="outline"
                  onClick={() => downloadTemplate('companies')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('pages.importExport.template','Template')}
                </Button>
              </div>
            </div>

            {/* Export Section */}
            <div className="p-4 bg-gray-50 border rounded-lg">
              <h4 className="font-medium mb-2">{t('pages.importExport.exportCompanies','Export Companies')}</h4>
              <p className="text-sm text-gray-600 mb-3">
                {t('pages.importExport.exportCompaniesDesc','Download all your companies as a CSV file')}
              </p>
              <Button
                onClick={() => handleExport('companies')}
                variant="outline"
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('pages.importExport.exportCsv','Export to CSV')}
              </Button>
            </div>

            {/* Info */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-medium mb-1">CSV Format:</p>
                <p>name, domain, industry, size, phone, address, notes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tips Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Import Tips</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-700">
          <p>• <strong>LeadGen Pro:</strong> Exports work automatically! No conversion needed 🎉</p>
          <p>• <strong>CSV Format:</strong> Make sure your file is comma-separated (.csv)</p>
          <p>• <strong>Headers:</strong> First row should contain column names</p>
          <p>• <strong>Required Fields:</strong> Name is required for all imports</p>
          <p>• <strong>Smart Mapping:</strong> Industry, Source, Score → converted to tags</p>
          <p>• <strong>Additional Data:</strong> Title, Location, LinkedIn → saved as notes</p>
          <p>• <strong>Template:</strong> Download the template to see the standard format</p>
          <p>• <strong>Large Files:</strong> For files with 1000+ records, import in batches</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportExport;
